/**
 * ScheduleListSection — Section inside AuditDetailPage.
 *
 * Shows compact schedule cards for an audit. Clicking a card navigates
 * to the dedicated ScheduleDetailPage for question assignment and config.
 *
 * Compact 2-row card layout:
 *   Row 1: Name + frequency badge + inactive badge
 *   Row 2: Auditor, date range, question count, action buttons
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/core/LanguageContext';
import { useSchedulesByAuditId, useCreateSchedule, useUpdateSchedule, useDeleteSchedule } from '../hooks/useSchedules';
import type { LpaScheduleWithDetails, LpaScheduleFrequency } from '../types';
import ScheduleFormDialog from './ScheduleFormDialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  CalendarClock,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
  Loader2,
  ListChecks,
  CalendarDays,
  HelpCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Frequency config
// ---------------------------------------------------------------------------

const FREQUENCY_CONFIG: Record<
  LpaScheduleFrequency,
  { labelKey: string; fallback: string; className: string }
> = {
  per_shift: {
    labelKey: 'mod_lpa.schedules.freq.perShift',
    fallback: 'Per Shift',
    className: 'bg-violet-500/15 text-violet-700 border-violet-300 dark:text-violet-400 dark:border-violet-600',
  },
  daily: {
    labelKey: 'mod_lpa.schedules.freq.daily',
    fallback: 'Daily',
    className: 'bg-blue-500/15 text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-600',
  },
  weekly: {
    labelKey: 'mod_lpa.schedules.freq.weekly',
    fallback: 'Weekly',
    className: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-600',
  },
  monthly: {
    labelKey: 'mod_lpa.schedules.freq.monthly',
    fallback: 'Monthly',
    className: 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-600',
  },
  quarterly: {
    labelKey: 'mod_lpa.schedules.freq.quarterly',
    fallback: 'Quarterly',
    className: 'bg-rose-500/15 text-rose-700 border-rose-300 dark:text-rose-400 dark:border-rose-600',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Schedule card — compact 2-row layout
// ---------------------------------------------------------------------------

interface ScheduleCardProps {
  schedule: LpaScheduleWithDetails;
  auditId: string;
  isArchived: boolean;
  onEdit: (schedule: LpaScheduleWithDetails) => void;
  onDelete: (schedule: LpaScheduleWithDetails) => void;
  t: (key: string, fallback: string) => string;
}

function ScheduleCard({
  schedule,
  auditId,
  isArchived,
  onEdit,
  onDelete,
  t,
}: ScheduleCardProps) {
  const navigate = useNavigate();
  const updateSchedule = useUpdateSchedule();

  const freqConfig = FREQUENCY_CONFIG[schedule.frequency];

  const handleActiveToggle = (checked: boolean) => {
    updateSchedule.mutate({
      scheduleId: schedule.id,
      auditId,
      input: { is_active: checked },
    });
  };

  const handleCardClick = () => {
    navigate(`/lpa/audits/${auditId}/schedules/${schedule.id}`);
  };

  return (
    <div
      className={`rounded-lg border hover:border-primary/40 transition-colors cursor-pointer ${!schedule.is_active ? 'opacity-60' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Row 1: Name + badges */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        <span className="font-medium text-sm truncate">{schedule.name}</span>
        <Badge variant="outline" className={`text-xs shrink-0 ${freqConfig.className}`}>
          {t(freqConfig.labelKey, freqConfig.fallback)}
        </Badge>
        {!schedule.is_active && (
          <Badge variant="secondary" className="text-xs shrink-0">
            {t('mod_lpa.schedules.inactive', 'Inactive')}
          </Badge>
        )}
      </div>

      {/* Row 2: Auditor, dates, question count, actions */}
      <div className="flex items-center gap-3 px-4 pb-3 pt-1">
        {/* Auditor */}
        <div className="flex items-center gap-1.5 shrink-0">
          {schedule.auditor ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[10px]">
                        {getInitials(schedule.auditor.full_name, schedule.auditor.email)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-muted-foreground max-w-[100px] truncate">
                      {schedule.auditor.full_name ?? schedule.auditor.email}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {schedule.auditor.full_name ?? schedule.auditor.email}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Badge
              variant="outline"
              className="text-xs border-amber-300 text-amber-600 dark:border-amber-600 dark:text-amber-400"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              {t('mod_lpa.schedules.noAuditor', 'No auditor')}
            </Badge>
          )}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <CalendarDays className="h-3 w-3" />
          <span>
            {formatDate(schedule.start_date)}
            {schedule.end_date ? ` – ${formatDate(schedule.end_date)}` : '+'}
          </span>
        </div>

        {/* Question count */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <ListChecks className="h-3.5 w-3.5" />
                <span>
                  {schedule.active_question_count}/{schedule.question_count}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {t('mod_lpa.schedules.questionCountTooltip', '{active} active / {total} total questions')
                .replace('{active}', String(schedule.active_question_count))
                .replace('{total}', String(schedule.question_count))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions — stop propagation so clicks don't navigate */}
        {!isArchived && (
          <div
            className="flex items-center gap-1 shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={schedule.is_active}
              onCheckedChange={handleActiveToggle}
              disabled={updateSchedule.isPending}
              className="scale-75"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEdit(schedule)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => onDelete(schedule)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function ScheduleListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <Skeleton key={i} className="h-[68px] w-full rounded-lg" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — minimal, single button
// ---------------------------------------------------------------------------

function EmptyState({ t, onAdd }: { t: (key: string, fallback: string) => string; onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg border border-dashed">
      <CalendarClock className="h-7 w-7 text-muted-foreground/50 mb-2" />
      <p className="text-sm text-muted-foreground mb-3">
        {t('mod_lpa.schedules.empty', 'No schedules configured yet. Add a schedule layer to define who audits, how often, and which questions.')}
      </p>
      <Button size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-1.5" />
        {t('mod_lpa.schedules.addFirst', 'Add First Schedule')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main section
// ---------------------------------------------------------------------------

interface ScheduleListSectionProps {
  auditId: string;
  isArchived?: boolean;
}

export default function ScheduleListSection({
  auditId,
  isArchived = false,
}: ScheduleListSectionProps) {
  const { t } = useLanguage();
  const { data: schedules, isLoading } = useSchedulesByAuditId(auditId);
  const createSchedule = useCreateSchedule();
  const deleteScheduleHook = useDeleteSchedule();

  // UI state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<LpaScheduleWithDetails | null>(null);
  const [deletingSchedule, setDeletingSchedule] = useState<LpaScheduleWithDetails | null>(null);

  const updateSchedule = useUpdateSchedule();

  // Handlers
  const handleCreate = (data: {
    name: string;
    frequency: LpaScheduleFrequency;
    auditor_id: string | null;
    is_active: boolean;
    start_date: string;
    end_date: string | null;
  }) => {
    createSchedule.mutate(
      {
        audit_id: auditId,
        name: data.name,
        frequency: data.frequency,
        auditor_id: data.auditor_id,
        is_active: data.is_active,
        start_date: data.start_date,
        end_date: data.end_date,
      },
      { onSuccess: () => setCreateDialogOpen(false) },
    );
  };

  const handleEdit = (data: {
    name: string;
    frequency: LpaScheduleFrequency;
    auditor_id: string | null;
    is_active: boolean;
    start_date: string;
    end_date: string | null;
  }) => {
    if (!editingSchedule) return;
    updateSchedule.mutate(
      {
        scheduleId: editingSchedule.id,
        auditId,
        input: {
          name: data.name,
          frequency: data.frequency,
          auditor_id: data.auditor_id,
          is_active: data.is_active,
          start_date: data.start_date,
          end_date: data.end_date,
        },
      },
      { onSuccess: () => setEditingSchedule(null) },
    );
  };

  const handleDelete = () => {
    if (!deletingSchedule) return;
    deleteScheduleHook.mutate(
      { scheduleId: deletingSchedule.id, auditId },
      { onSuccess: () => setDeletingSchedule(null) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t('mod_lpa.audits.detail.schedulesTitle', 'Schedules')}
            </CardTitle>
            {schedules && schedules.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {schedules.length}
              </Badge>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-80 text-sm">
                <p className="text-xs text-muted-foreground">
                  {t(
                    'mod_lpa.audits.detail.schedulesDescription',
                    'Recurring audit schedules define who audits, how often, and which questions. Each schedule represents a layer (e.g., operator daily, supervisor weekly).',
                  )}
                </p>
              </PopoverContent>
            </Popover>
          </div>
          {!isArchived && (
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              {t('mod_lpa.schedules.add', 'Add Schedule')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ScheduleListSkeleton />
        ) : !schedules || schedules.length === 0 ? (
          <EmptyState t={t} onAdd={() => setCreateDialogOpen(true)} />
        ) : (
          <div className="space-y-2">
            {schedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                auditId={auditId}
                isArchived={isArchived}
                onEdit={setEditingSchedule}
                onDelete={setDeletingSchedule}
                t={t}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Create dialog */}
      <ScheduleFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreate}
        isSubmitting={createSchedule.isPending}
      />

      {/* Edit dialog */}
      <ScheduleFormDialog
        open={!!editingSchedule}
        onOpenChange={(open) => !open && setEditingSchedule(null)}
        schedule={editingSchedule}
        onSubmit={handleEdit}
        isSubmitting={updateSchedule.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deletingSchedule}
        onOpenChange={(open) => !open && setDeletingSchedule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('mod_lpa.schedules.deleteTitle', 'Delete schedule?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'mod_lpa.schedules.deleteDescription',
                `This will permanently delete "${deletingSchedule?.name}" and all its question assignments. This action cannot be undone.`,
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('mod_lpa.schedules.deleteCancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteScheduleHook.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('mod_lpa.schedules.deleteConfirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
